import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useUploadFiles} from 'helpers/hooks/utils';
import StyledEntitiesUploadWizardContent
  from 'components/organisms/WizardContent/EntitiesUploadWizardContent/EntitiesUploadWizardContent.styles';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import {useSourcePreProcess, useSourceUpload} from 'services/source/source.hooks';
import Box from 'components/atoms/Layout/Box/Box';
import {H6} from 'components/atoms/Text/Typography/Typography';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import sample from 'assets/samples/CatalistUploadTemplate.xlsx';
import Link from 'components/atoms/Links/Link/Link';
import SimpleLoader from 'components/molecules/Loaders/SimpleLoader/SimpleLoader';

const EntitiesUploadWizardContent = React.forwardRef((props, ref) => {
  const {
    wizard,
    step,
    isSource,
    file,
    onComplete,
    onError,
    onDirty,
    ...innerProps
  } = useComponentProps(props, 'EntitiesUploadWizardContent');

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const data = wizard.data[wizard.dataKey];
  const state = wizard.getStepState();
  const collectionId = data.params.collectionId;

  const snackbar = useSnackbar();
  const uploadSource = useSourceUpload();
  const preProcessFileSource = useSourcePreProcess();
  const [uploadState, setUploadState, uploadFiles] = useUploadFiles(state.uploadState);

  const uploadFilesEvent = useEffectEvent(uploadFiles);
  const onErrorEvent = useEffectEvent(onError);
  const setDataEvent = useEffectEvent(wizard.setData);
  const setStateEvent = useEffectEvent(wizard.setStepState);

  const uploadWizardContent = useMemo(() => ({
    refs: {
      ref: innerRef,
      formRef: formRef
    },
    submit: () => {
      return formRef.current?.submit()
        .catch(() => {
          onErrorEvent?.('Please check if all fields have the correct values');
        });
    }
  }), [onErrorEvent]);

  useImperativeHandle(ref, () => uploadWizardContent);

  const FileFieldProps = useMemo(() => {
    const fileState = uploadState ?
      Object.keys(uploadState).reduce((o, k) => {
        const split = k.split('_');
        o[split[1]] = uploadState[k];
        return o;
      }, {}) : null;

    return {
      FormFieldProps: {
        status: fileState,
        minSize: 1,
        maxSize: constants.numbers.GB / 2
      }
    }
  }, [uploadState]);

  const fields = useMemo(() => {
    const fields = [];

    fields.push({
      name: 'file',
      label: 'File',
      entity: !isSource ? 'entitiesUpload' : 'source',
      type: constants.formFieldTypes.file,
      validation: constants.formFieldValidationTypes.file,
      conversion: constants.formFieldConversionTypes.none,
      initial: data.file,
      validate: (value, testContext) => {
        if (state.fileError) {
          return testContext.createError({message: state.fileError});
        } else {
          return true;
        }
      },
      required: true,
      ...{
        ...FileFieldProps,
        FormFieldProps: {
          hiddenLabel: true,
          multiple: false,
          addDescription: false,
          fileDescription: 'Excel, Csv, Json, Word or Pdf document',
          types: ['xls', 'xlsx', 'csv', 'json', 'pdf', 'html', 'doc', 'docx', 'pptx', 'rtf', 'txt', 'tex'],
          ...FileFieldProps?.FormFieldProps
        }
      }
    });

    if (isSource) {
      fields.push({
        name: 'name',
        label: 'Name',
        entity: 'source',
        type: constants.formFieldTypes.text,
        initial: data.name ?? null,
        required: true,
        FormFieldProps: {
          fullWidth: false,
        }
      });
    }

    fields.push({
      name: 'existingEntities',
      label: 'What do you want to do with companies that already appear in the database?',
      entity: !isSource ? 'entitiesUpload' : 'source',
      type: constants.formFieldTypes.list,
      validation: constants.formFieldValidationTypes.list,
      conversion: constants.formFieldConversionTypes.value,
      initial: [
        data.params.fileEnrichProfiles ? 'fileEnrichProfiles' : null,
        data.params.fileOverwriteValues ? 'fileOverwriteValues' : null
      ].filter((_) => (_)),
      options: [
        {value: 'fileEnrichProfiles', label: 'Only add data points to existing companies, ignore any new companies in the file'},
        {value: 'fileOverwriteValues', label: 'Always overwrite existing company data points with the data points from the file'}
      ],
      FormFieldProps: {
        multiple: true,
        ListProps: {
          catchFocus: false
        }
      }
    });

    return utils.initializeFormFields(fields, {...data, ...state});
  }, [FileFieldProps, data, state, isSource]);

  const onDirtyEvent = useEffectEvent(onDirty);
  const resetNextSteps = useEffectEvent(wizard.resetNextSteps);
  const doChange = useCallback((field, value) => {
    onDirtyEvent?.(true);

    if (field) {
      if (field.name === 'file') {
        resetNextSteps?.();
        setUploadState(null);
        setStateEvent?.({fileError: null});

        const extraProps = !isSource ? {name: value?.name} : {};

        setDataEvent?.((current) => ({
          ...current,
          [wizard.dataKey]: {
            ...current[wizard.dataKey],
            file: value,
            fileId: null,
            fields: [],
            replacement: [],
            params: {
              ...current[wizard.dataKey].params,
              fileName: value?.name,
              config: null
            },
            ...extraProps,

            // try setting setting name
            name: (utils.isEmpty(current[wizard.dataKey]?.name) || (utils.filename2Name(current[wizard.dataKey]?.file?.name) === current[wizard.dataKey]?.name)) ? (
              value ? utils.filename2Name(value?.name) : ''
            ) : current[wizard.dataKey]?.name
          }
        }));
      } else if (field.name === 'existingEntities') {
        setDataEvent?.((current) => ({
          ...current,
          [wizard.dataKey]: {
            ...current[wizard.dataKey],
            params: {
              ...current[wizard.dataKey].params,
              fileEnrichProfiles: Boolean(utils.toArray(value).find((v) => v.value === 'fileEnrichProfiles')),
              fileOverwriteValues: Boolean(utils.toArray(value).find((v) => v.value === 'fileOverwriteValues'))
            }
          }
        }));
      } else {
        setDataEvent?.((current) => ({
          ...current,
          [wizard.dataKey]: {
            ...current[wizard.dataKey],
            [field.name]: value
          }
        }));
      }
    }
  }, [isSource, onDirtyEvent, resetNextSteps, setDataEvent, setStateEvent, setUploadState, wizard.dataKey]);

  const handleValidating = useCallback((isValidating, isDirty, hasErrors) => {
    if (hasErrors) {
      onErrorEvent?.('Please check if all fields have the correct values');
    } else {
      onErrorEvent?.(null);
    }
  }, [onErrorEvent]);

  const handleChangeDirect = useCallback((e) => {
    const field = fields?.find((field) => field.name === e?.target?.name);
    const value = e?.target?.value;

    doChange(field, value);
  }, [doChange, fields]);

  const onCompleteEvent = useEffectEvent(onComplete);
  const handleSubmit = useCallback((values, actions) => {
    onDirtyEvent?.(true, true); // set submitting, on complete or error resets

    if (!utils.isDefined(data.fileId)) {
      uploadSource.mutation.mutateAsync({
        filename: values.file?.name,
        filesize: values.file?.size,
        filetype: values.file?.type || constants.filetypes.default,
        tempFile: true
      })
        .then((res) => {
          const upload = utils.camelcase(res.response.data?.data);
          const name = values.file?.name;

          return uploadFilesEvent?.([{
            file: values.file,
            id: 'file_0',
            url: upload?.uploadUrl,
            name
          }], true)
            .then(() => {
              return preProcessFileSource.mutation.mutateAsync({
                collectionId,
                fileId: upload?.key
              })
                .then((res) => {
                  setDataEvent?.((current) => ({
                    ...current,
                    [wizard.dataKey]: {
                      ...current[wizard.dataKey],
                      fileId: upload?.key,
                      fields: utils.camelcase(res.response.data.data.fields),
                      params: {
                        ...current[wizard.dataKey].params,
                        config: utils.camelcaseEx(res.response.data.data.mapping, true)
                      }
                    }
                  }));
                  onCompleteEvent?.(Boolean(step?.resetNextSteps));
                });
            });
        })
        .catch(() => {
          setStateEvent?.({fileError: 'Please provide a valid file'});
          actions.setFieldError('file', 'Please provide a valid file');
          setUploadState((current) => ({
            ...current,
            file_0: {
              ...current?.file_0,
              success: false,
              error: true,
              loading: false
            }
          }));
          onErrorEvent?.('Upload failed.');
          snackbar.show('Invalid file, upload failed', null, {color: 'error', autoHideDuration: constants.delay.error});
        })
        .finally(() => {
          actions.setSubmitting(false);
        });
    } else {
      actions.setSubmitting(false);
      onCompleteEvent?.(Boolean(step?.resetNextSteps));
    }
  }, [collectionId, data.fileId, onCompleteEvent, onDirtyEvent, onErrorEvent,
    preProcessFileSource.mutation, setDataEvent, setStateEvent, setUploadState, snackbar,
    step?.resetNextSteps, uploadFilesEvent, uploadSource.mutation, wizard.dataKey]);

  useEffect(() => {
    setStateEvent?.({uploadState})
  }, [setStateEvent, uploadState]);

  const doChangeEvent = useEffectEvent(doChange);
  useEffect(() => {
    if (file && !data.file) {
      doChangeEvent?.({name: 'file'}, file)
    }
  }, [file, data.file, doChangeEvent]);

  const renderSubtitle = () => {
    return <Box className="EntitiesUploadWizardContent-subtitle">
      <H6>Do you have doubts about the structure of your file? Then <Link href="#" onClick={() => utils.fileDownload('sample.xlsx', sample)}>download</Link> our sample file</H6>
    </Box>
  };

  return <StyledEntitiesUploadWizardContent ref={ref} {...innerProps}>
    {renderSubtitle()}
    <InlineForm ref={formRef}
                fields={fields}
                onValidating={handleValidating}
                onChangeDirect={handleChangeDirect}
                onSubmit={handleSubmit}/>
    {wizard.state.submitting ? <SimpleLoader className="EntitiesUploadWizardContent-loader"
                                             titles={['Processing file']} /> : null}
  </StyledEntitiesUploadWizardContent>
});

EntitiesUploadWizardContent.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  wizard: PropTypes.object,
  step: PropTypes.object,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  onDirty: PropTypes.func,
};

export default EntitiesUploadWizardContent;
